import {
    Box,
    CircularProgress,
    Icon,
    IconButton,
    InputAdornment,
    List,
    ListItemButton,
    ListItemText,
    Typography,
} from "@mui/material";
import useRequest from "api/useRequest";
import { modalHide } from "app/App/actions";
import trans from "helpers/trans";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
    Modal,
    ModalActions,
    ModalContent,
    ModalTitle,
    RequestMessage,
} from "ui";
import { inventoryFetch } from "../actions";
import { InventoryReportModel } from "app/InventoryReport/types";
import { StyledOutlinedInput } from "app/Ingredients/components/Ingredients";
import moment from "moment";
import { AxiosResponse } from "axios";

interface IInventorySearchModal {
    onClick?: (inventory: InventoryReportModel) => void;
}

const InventorySearchModal = (props: IInventorySearchModal) => {
    const [data, setData] = useState([]);
    const dispatch = useDispatch();
    const [search, setSearch] = useState("");

    const { cancel, currentData, isError, isLoading, request, status } =
        useRequest();

    const onClose = () => {
        dispatch(modalHide("InventorySearchModal"));
    };

    useEffect(() => {
        request(
            inventoryFetch(
                {
                    params: {
                        _sort: { id: "desc" },
                        per_page: 10,
                        page: 1,
                    },
                },
                (response: AxiosResponse) => {
                    if (response.status !== 200) return;

                    setData(response.data.data);
                },
            ),
        );
        // eslint-disable-next-line
    }, []);

    const onChangeSearch = (value: string) => {
        cancel();

        if (value.length > 0) {
            setSearch(value);

            request(
                inventoryFetch({
                    params: {
                        _search: value,
                        per_page: 10,
                        page: 1,
                        _sort: { id: "desc" },
                    },
                }),
            );
        } else {
            setSearch("");
            setData([]);
        }

        // setSearchValue(value);
    };

    const onChooseInventory = (inventory: InventoryReportModel) => {
        if (!!props.onClick) {
            dispatch(modalHide("InventorySearchModal"));
            props.onClick(inventory);
        }
    };

    useEffect(() => {
        if (!isLoading && status === 200) {
            setData(currentData.data);
        }
    }, [currentData?.data, isLoading, status]);

    const renderContent = () => {
        if (isLoading) return null;

        if (isError)
            return status === 404 ? (
                <Typography variant="body1" sx={{ textAlign: "center", mb: 2 }}>
                    {trans("inv.inventory.modal.InventorySearchModal.notFound")}
                </Typography>
            ) : (
                <RequestMessage status={status} />
            );

        return (
            <>
                {data.map((item: InventoryReportModel) => {
                    return (
                        <ListItemButton
                            sx={{
                                flexGrow: 1,
                            }}
                            onClick={() => onChooseInventory(item)}
                            key={`inventory-search-item-${item.id}`}
                        >
                            <ListItemText
                                primary={item.name}
                                secondary={`${moment(item.created_at).format(
                                    "DD/MM/YYYY HH:mm",
                                )} - ${item.end_date ? moment(item.end_date).format("DD/MM/YYYY HH:mm") : ""}`}
                            />
                        </ListItemButton>
                    );
                })}
            </>
        );
    };

    const renderEndAdornment = () => {
        if (isLoading) {
            return (
                <InputAdornment position="end">
                    <CircularProgress size={20} />
                </InputAdornment>
            );
        }

        if (search.length > 0) {
            return (
                <InputAdornment position="end">
                    <IconButton
                        onClick={() => onChangeSearch("")}
                        sx={{
                            position: "absolute",
                            right: "0",
                        }}
                    >
                        <Icon>close</Icon>
                    </IconButton>
                </InputAdornment>
            );
        }
    };

    return (
        <Modal
            maxWidth="xs"
            fullWidth
            open
            PaperProps={{ sx: { position: "fixed", top: 0 } }}
        >
            <ModalTitle onClose={onClose}>
                {trans("inv.inventory.modal.InventorySearchModal.search")}
            </ModalTitle>
            <ModalActions
                sx={{
                    px: 2,
                    gap: 1,
                    display: "flex",
                    alignItems: "flex-start",
                    flexDirection: "column",
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        width: "100%",
                        gap: 1,
                    }}
                >
                    <StyledOutlinedInput
                        autoFocus
                        endAdornment={renderEndAdornment()}
                        margin="dense"
                        onChange={(e) => onChangeSearch(e.target.value)}
                        placeholder={trans(
                            "inv.inventory.modal.InventorySearchModal.placeholder",
                        )}
                        size="small"
                        startAdornment={
                            <InputAdornment position="start">
                                <Icon sx={{ color: "grey.400" }}>search</Icon>
                            </InputAdornment>
                        }
                        value={search}
                    />
                </Box>
            </ModalActions>
            <ModalContent sx={{ py: 0, px: 2, position: "relative" }}>
                <List>{renderContent()}</List>
            </ModalContent>
        </Modal>
    );
};

export default InventorySearchModal;
